// @react
import React, { ReactElement, useRef } from 'react'
// @libraries
import { Controller, UseFormMethods, Control } from 'react-hook-form'
import creditCardType from 'credit-card-type'
// @data
import { card_type_options } from 'constants/formValues'
// @types
import { AutoRenewFormValues } from 'modules/common/types'
// @components
import Grid from '@bit/atd.web.grid'
import InputText from '@bit/atd.web.forms.input-text'
import Select from '@bit/atd.web.select'
// @module components
import CreditCardSecurityField from './CreditCardSecurity'

type PropsType = {
  formMethods: UseFormMethods<AutoRenewFormValues>
  control: Control<AutoRenewFormValues>
}

const CreditCard = ({ formMethods, control }: PropsType): ReactElement => {
  /******************************
   *  PROPS
   *******************************/

  const { formState } = formMethods
  const { errors } = formState

  /******************************
   *  REFS
   *******************************/

  const cardTypeRef = useRef<HTMLInputElement | null>(null)
  const cardNumberRef = useRef<HTMLInputElement | null>(null)
  const cardExpirationDateRef = useRef<HTMLInputElement | null>(null)

  /******************************
   *  RENDER
   *******************************/

  return (
    <>
      {/* Card number */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          onFocus={() => cardNumberRef?.current?.focus()}
          name="cardNumber"
          render={({ onChange, onBlur, value, name }) => (
            <InputText
              required
              name={name}
              label="Card Number"
              placeholder="Enter your card number."
              onBlur={onBlur}
              onChange={(event) => {
                if (creditCardType(event.target.value)[0]) {
                  formMethods.setValue(
                    'cardType',
                    creditCardType(event.target.value)[0].type,
                    {
                      shouldValidate: true,
                    },
                  )
                }
                return onChange(event)
              }}
              value={value}
              inputRef={cardNumberRef}
              error={!!errors?.cardNumber}
              errorText={errors?.cardNumber?.message}
            />
          )}
        />
      </Grid>

      {/* Card type */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          onFocus={() => cardTypeRef?.current?.focus()}
          name="cardType"
          render={({ onChange, onBlur, value, name }) => (
            <Select
              native
              required
              name={name}
              label="Card Type"
              placeholder="Choose your card type."
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              inputRef={cardTypeRef}
              options={card_type_options}
              error={!!errors?.cardType}
              errorText={errors?.cardType?.message}
            />
          )}
        />
      </Grid>

      {/* Expiration Month / Year */}
      <Grid xs={12} sm={6} item>
        <Grid container spacing={2}>
          <Grid xs={12} sm={7} item>
            <Controller
              control={control}
              onFocus={() => cardExpirationDateRef?.current?.focus()}
              name="cardExpirationDate"
              render={({ onChange, onBlur, value, name }) => (
                <InputText
                  required
                  name={name}
                  label="Expiration Date"
                  placeholder="MM/YY"
                  mask="99/99"
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  inputRef={cardExpirationDateRef}
                  error={!!errors?.cardExpirationDate}
                  errorText={errors?.cardExpirationDate?.message}
                />
              )}
            />
          </Grid>

          {/* Card Security Code */}
          <CreditCardSecurityField
            formMethods={formMethods}
            control={control}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default CreditCard
